import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { MuiBreadcrumbEntry } from './mui-breadcrumb-entry';
import { MuiBreadcrumbItem } from './mui-breadcrumb-item.component';

@Injectable({
  providedIn: 'root'
})
export class MuiBreadcrumbsService {
  get breadcrumbs$(): Observable<MuiBreadcrumbEntry[]> {
    return this._breadcrumbs.asObservable();
  }

  get depth$(): Observable<number> {
    return this._breadcrumbs.asObservable().pipe(map((bcs) => bcs.length));
  }

  homeLabel = 'Home';
  labelDataProperty = 'breadcrumb';

  private _breadcrumbs = new BehaviorSubject<MuiBreadcrumbEntry[]>([]);
  private readonly _routerEvent$: Observable<MuiBreadcrumbEntry[]>;

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {
    this._routerEvent$ = this.createRouterEventObservable();
    this._routerEvent$.subscribe((breadcrumbs) => this._breadcrumbs.next(breadcrumbs));

    /* Generate initial breadcrumbs before first navigation event */
    this.findInitialBreadcrumb();
  }

  private static castStringToObservable(val: Observable<string> | string): Observable<string> {
    return typeof val === 'string' ? of(val) : val;
  }

  private static getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return (
      '/' +
      route.pathFromRoot
        .filter((v) => v.url.length > 0)
        .map((v) => v.url.map((segment) => segment.toString()).join('/'))
        .join('/')
    );
  }

  addCustomItem(item: MuiBreadcrumbEntry): void {
    const current = this._breadcrumbs.value;
    current.push(item);
    this._breadcrumbs.next(current);
  }

  private findInitialBreadcrumb(): void {
    let route = this.router.routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    this._breadcrumbs.next(this.buildBreadcrumb(route));
  }

  private buildBreadcrumb(route: ActivatedRoute): MuiBreadcrumbEntry[] {
    const routes = route.pathFromRoot;
    const res = [];

    for (const current of routes) {
      if (current.parent === null) {
        // Set the root path as the home entry of the breadcrumbs
        res.push({ label: of(this.homeLabel), url: '/', route: current });
      } else {
        // Only add breadcrumbs for routes with the data[this.labelDataProperty] defined
        if (this.hasBreadcrumbDefinition(current)) {
          res.push(this.createCrumbObject(current.snapshot.data[this.labelDataProperty], current));
        }
      }
    }

    return res;
  }

  private createCrumbObject(label: Observable<string> | string, route: ActivatedRoute): MuiBreadcrumbEntry {
    return {
      url: MuiBreadcrumbsService.getResolvedUrl(route.snapshot),
      label: MuiBreadcrumbsService.castStringToObservable(label).pipe(takeUntil(this._routerEvent$)),
      route
    };
  }

  /**
   * Create an Observable that listens to the Router events and builds a breadcrumb based on the ActivatedRoute.
   */
  private createRouterEventObservable(): Observable<MuiBreadcrumbEntry[]> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      distinctUntilChanged(),
      map(() => {
        // Get the deepest route child from the activated route
        let route: ActivatedRoute = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      map((route) => this.buildBreadcrumb(route))
    );
  }

  private hasBreadcrumbDefinition(route: ActivatedRoute): boolean {
    return (
      (route.snapshot && route.snapshot.data &&
        route.snapshot.data[this.labelDataProperty] &&
        route.routeConfig?.data &&
        route.routeConfig.data[this.labelDataProperty]) ||
      (route.routeConfig?.resolve && route.routeConfig.resolve[this.labelDataProperty])
    );
  }
}
